import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Moon, Sun, X, RefreshCw, Plus } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";
import { canvasToBMP } from "../utils/imageConverters";

export default function ConvertSvgToBmp() {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [scale, setScale] = useState([2]);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Convert SVG to BMP - KayTool";
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.type === 'image/svg+xml' || f.name.toLowerCase().endsWith('.svg')
    );
    if (files.length > 0) processImages(files);
    else toast.error("Please drop SVG files only");
  };

  const processImages = async (files) => {
    setIsProcessing(true);
    setProgress(0);
    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      try {
        setProgress(Math.round((i / files.length) * 100));
        const file = files[i];
        
        const svgText = await file.text();
        const blob = new Blob([svgText], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
        
        const width = img.width * scale[0];
        const height = img.height * scale[0];
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        
        const bmpBlob = canvasToBMP(canvas);
        const convertedFile = new File([bmpBlob], file.name.replace(/\.svg$/i, '.bmp'), { type: 'image/bmp' });
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalSize: file.size,
          convertedFile,
          convertedSize: convertedFile.size,
          preview: URL.createObjectURL(bmpBlob),
          dimensions: { width, height }
        });
      } catch (error) {
        console.error(`Error converting ${files[i].name}:`, error);
        toast.error(`Failed to convert ${files[i].name}`);
      }
    }

    setProgress(100);
    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    if (newImages.length > 0) toast.success(`Converted ${newImages.length} image(s) to BMP!`);
  };

  const downloadImage = (image) => saveAs(image.preview, image.convertedFile.name);

  const downloadAllAsZip = async () => {
    const zip = new JSZip();
    for (const img of images) {
      const response = await fetch(img.preview);
      const blob = await response.blob();
      zip.file(img.convertedFile.name, blob);
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "svg-to-bmp-converted.zip");
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 dark:text-white">SVG to BMP</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <QuickConvert />
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 border-emerald-200 text-emerald-600 bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:bg-emerald-900/20">
              <RefreshCw className="w-3 h-3" /> SVG → BMP
            </Badge>
            <Button variant="ghost" size="icon" onClick={() => setDarkMode(prev => !prev)}>
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-3">Convert SVG to BMP</h2>
          <p className="text-slate-600 dark:text-slate-300">Convert vector SVG files to BMP format. Free and private.</p>
        </div>

        <Card className="p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-xs mx-auto mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Scale</span>
            <span className="text-sm font-bold text-emerald-600">{scale[0]}x</span>
          </div>
          <Slider value={scale} onValueChange={setScale} min={1} max={4} step={0.5} className="w-full" />
        </Card>

        <div
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer
            ${isDragging ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-emerald-400 bg-white dark:bg-slate-800'}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".svg,image/svg+xml" multiple onChange={(e) => { processImages(Array.from(e.target.files)); e.target.value = ''; }} className="hidden" />
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-emerald-600' : 'text-slate-400'}`} />
          <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Drop SVG files here or click to upload</p>
          <p className="text-sm text-slate-500 mt-1">Multiple files supported</p>
        </div>

        {isProcessing && (
          <div className="mt-6">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 text-center">Converting... {progress}%</p>
          </div>
        )}

        {images.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Converted Images ({images.length})</h3>
              <div className="flex gap-2">
                <input ref={addMoreInputRef} type="file" accept=".svg,image/svg+xml" multiple onChange={(e) => { processImages(Array.from(e.target.files)); e.target.value = ''; }} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => addMoreInputRef.current?.click()} className="text-green-600 border-green-200">
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
                <Button variant="outline" size="sm" onClick={() => setImages([])} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                {images.length > 1 && (
                  <Button size="sm" onClick={downloadAllAsZip} className="bg-emerald-600 hover:bg-emerald-700">
                    <Download className="w-4 h-4 mr-1" /> Download All (ZIP)
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800">
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img src={image.preview} alt={image.name} className="w-full h-full object-contain" />
                    <button onClick={() => setImages(prev => prev.filter(i => i.id !== image.id))} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
                      <X className="w-4 h-4" />
                    </button>
                    <Badge className="absolute bottom-2 left-2 bg-emerald-600">BMP</Badge>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate text-sm">{image.name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <span>{formatSize(image.originalSize)}</span>
                      <span>→</span>
                      <span className="font-medium text-emerald-600">{formatSize(image.convertedSize)}</span>
                    </div>
                    <Button onClick={() => downloadImage(image)} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700" size="sm">
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2025 KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>
    </div>
  );
}
